import { useState, useEffect } from 'react'
import { fb } from './firebase'
import { getPlanById, Plan, hasPolygonlePlus } from './plans'
import { getFirestoreUser, User as FirestoreUser } from './firestore'
import { getTrueTime } from './time'
import { setStats as setFirestoreStats } from './firestore'
import {
  getStoredIsSignedIn,
  setStoredIsSignedIn,
  getStoredStats,
  setStoredStats,
  GameStats,
  GAME_STAT_TYPE_USER,
} from './localStorage'
import { MAX_CHALLENGES } from '../constants/settings'

// TODO dedup this
const DEFAULT_STATS: GameStats = {
  winDistribution: Array.from(new Array(MAX_CHALLENGES), () => 0),
  gamesFailed: 0,
  currentStreak: 0,
  bestStreak: 0,
  totalGames: 0,
  successRate: 0,
}

// TODO write tests
const getPremiumPlan = async (fsUser: FirestoreUser) => {
  const time = await getTrueTime()
  // TODO: handle precedence of multiple subs
  const validSub = (fsUser.subscriptions ?? []).find((s) => {
    const cancelled = s.date_end && new Date(s.date_end) < time
    const activeSubscription = s.date_create && new Date(s.date_create) < time
    return activeSubscription && !cancelled
  })
  if (!validSub) {
    return null
  }
  return getPlanById(validSub.plan_id)
}

const loadStats = (fsUser: FirestoreUser, hasPolygonlePlus: boolean) => {
  const userLocalStats = getStoredStats(fsUser.uid) || DEFAULT_STATS
  if (!hasPolygonlePlus) {
    userLocalStats.type = GAME_STAT_TYPE_USER
    return userLocalStats
  }
  if (!fsUser.stats) {
    // first time user
    return DEFAULT_STATS
  }
  // Allow overriding local stats from the server
  if (fsUser.stats.override) {
    setStoredStats(fsUser.stats, fsUser.uid)
    let stats = fsUser.stats
    stats.type = GAME_STAT_TYPE_USER
    delete stats.override
    return stats
  }
  const firestoreGames = fsUser.stats.totalGames ?? 0
  const localGames = userLocalStats.totalGames ?? 0
  const stats = firestoreGames >= localGames ? fsUser.stats : userLocalStats
  if (firestoreGames < localGames) {
    // firestore out-of-sync
    // write local to firestore
    setFirestoreStats(fsUser.uid, userLocalStats)
  } else if (firestoreGames < localGames) {
    // local out-of-sync
    // overwrite local with firestore
    setStoredStats(stats, fsUser.uid)
  }
  stats.type = GAME_STAT_TYPE_USER
  return stats
}

export const anonUserFactory = () => {
  const stats = getStoredStats(null) || DEFAULT_STATS
  return new AnonUser(stats)
}

export const authedUserFactory = async (fsUser: FirestoreUser) => {
  const plan = await getPremiumPlan(fsUser)
  const hasPPlus = hasPolygonlePlus(plan)
  const stats = loadStats(fsUser, hasPPlus)
  return new AuthedUser(fsUser, stats, plan)
}

export interface User {
  stats(): GameStats
  hasPolygonlePlus(): boolean
  isAuthed(): boolean
  cloneWithNewStats(stats: GameStats): User // returns a copy with updated stats
}

export class AnonUser implements User {
  constructor(private gameStats: GameStats) {}

  public stats(): GameStats {
    return this.gameStats
  }

  public hasPolygonlePlus() {
    return false
  }

  public isAuthed() {
    return false
  }

  public cloneWithNewStats(stats: GameStats) {
    return new AnonUser(stats)
  }
}

export class AuthedUser implements User {
  constructor(
    private fsUser: FirestoreUser,
    private gameStats: GameStats,
    public premiumPlan: Plan | null
  ) {}

  public uid() {
    return this.fsUser.uid
  }

  public displayName() {
    return this.fsUser.displayName ?? ''
  }

  public email() {
    return this.fsUser.email ?? ''
  }

  public subscriptions() {
    return this.fsUser.subscriptions ?? []
  }

  public fbStats() {
    return this.fsUser.stats
  }

  public stats() {
    return this.gameStats
  }

  public firestoreUser() {
    return this.fsUser
  }

  public hasPolygonlePlus() {
    return hasPolygonlePlus(this.premiumPlan)
  }

  public isAuthed() {
    return true
  }

  public cloneWithNewStats(stats: GameStats) {
    return new AuthedUser(this.fsUser, stats, this.premiumPlan)
  }
}

// TODO incorporate stats into the user
export function useUser() {
  const [user, setUser] = useState<User>(anonUserFactory)
  const [signedIn, setSignedIn] = useState(false)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    const previouslySignedIn = getStoredIsSignedIn()
    if (!previouslySignedIn) {
      // shortcut for users who have never signed
      setSignedIn(false)
      setLoading(false)
    }
    // if no user in localstorage
    // just instantly set loading to false
    const callback = fb.getAuth().onAuthStateChanged((user) => {
      const isSignedIn = !!user
      setSignedIn(isSignedIn)
      setStoredIsSignedIn(isSignedIn)
      if (!isSignedIn) {
        setLoading(false)
      }
    })
    return callback
  }, [])

  useEffect(() => {
    getFirestoreUser().then(async (u) => {
      if (!u) {
        setUser(anonUserFactory())
        return
      }
      setUser(await authedUserFactory(u))
      setLoading(false)
    })
  }, [signedIn])

  return { user, setUser, loading }
}
